import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext, View } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { triggerHaptic } from '../../utils/haptics';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from './LanguageSelector';

export const Header: React.FC = () => {
  const { currentGroup, goToPage, view, db, setIsMenuOpen } = useAppContext();
  const { user, logout, userData } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const wl = db.globalConfig?.whiteLabel;
  const headerLogo = (wl?.enabled && wl?.logos?.currentLogoUrl) ? wl.logos.currentLogoUrl : '/app_title_logo.webp';
  const appName = (wl?.enabled && wl?.appName) ? wl.appName : 'Padel Brothers';

  // Use userData from Auth first (most reliable), then fall back to group players
  const currentPlayer = userData || currentGroup?.players?.find(p => p.id === user?.uid) ||
    db.groups.flatMap(g => g.players || []).find(p => p.id === user?.uid);

  const handleProfileClick = () => {
    triggerHaptic();
    setIsMenuOpen(false);
    goToPage(View.PROFILE);
  };

  const handleMenuToggle = () => {
    triggerHaptic();
    setIsMenuOpen(true);
  };

  const handleBackClick = () => {
    triggerHaptic();
    navigate(-1);
  };

  const handleLogout = () => {
    triggerHaptic('warning');
    logout();
  };

  const showBackButton = location.pathname !== '/' && location.pathname !== '/dashboard';

  return (
    <>
      <header className="sticky top-0 z-30 bg-background-alpha backdrop-blur-md border-b border-glass-border safe-area-pt">
        <div className="w-full px-4 py-1.5 flex items-center justify-between">
          {/* Left: Hamburger Menu & Back Button - Hidden if not logged in */}
          <div className="flex-1 flex items-center justify-start gap-1">
            {user && (
              <>
                <button
                  onClick={handleMenuToggle}
                  className="p-3 min-w-[48px] min-h-[48px] rounded-full hover:bg-surface-2 transition-colors lg:hidden flex items-center justify-center"
                >
                  <span className="material-symbols-outlined text-2xl">menu</span>
                </button>
                {showBackButton && (
                  <button
                    onClick={handleBackClick}
                    className="p-2 min-w-[48px] min-h-[48px] rounded-full bg-surface-2/50 hover:bg-primary hover:text-white transition-all backdrop-blur-sm border border-white/10 shadow-sm flex items-center justify-center group"
                    title={t('common.back', 'Retour')}
                  >
                    <span className="material-symbols-outlined text-sm group-hover:-translate-x-0.5 transition-transform">arrow_back_ios_new</span>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Center: Logo - Hidden on desktop ONLY if sidebar is present (user logged in) */}
          <div
            className={`flex-shrink-0 flex items-center justify-center cursor-pointer ${user ? 'lg:hidden' : ''}`}
            onClick={() => goToPage(View.HUB)}
          >
            <img
              src={headerLogo}
              alt={appName}
              className="h-16 lg:h-20 object-contain hover:scale-105 transition-transform duration-300 drop-shadow-md"
              width="160"
              height="80"
              fetchPriority="high"
            />
          </div>

          {/* Right: Notifications + Profile */}
          <div className="flex-1 flex items-center justify-end gap-3">
            {!user && <LanguageSelector />}

            {user ? (
              <>
                {view === View.PROFILE ? (
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-full bg-surface-2 hover:bg-danger/20 hover:text-danger transition-colors text-text-secondary flex-shrink-0"
                    title={t('auth.logout', 'Se déconnecter')}
                  >
                    <span className="material-symbols-outlined">logout</span>
                  </button>
                ) : (
                  <div className="block flex-shrink-0">
                    <NotificationBell />
                  </div>
                )}

                {/* Profile Avatar */}
                <button
                  onClick={handleProfileClick}
                  title={t('navbar.myProfile', 'Mon profil')}
                  className={`flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-primary text-white font-bold overflow-hidden border-2 transition-all ${view === View.PROFILE ? 'border-primary-hover ring-2 ring-primary/30' : 'border-primary hover:border-primary-hover'}`}
                >
                  {(() => {
                    // Check for URL-based avatar (photoURL or avatar that starts with http)
                    const photoUrl = currentPlayer?.photoURL || currentPlayer?.avatar;
                    const isUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('data:image') || photoUrl.startsWith('/'));

                    if (isUrl) {
                      return <img src={photoUrl} className="w-full h-full object-cover" alt="Profile" loading="lazy" decoding="async" />;
                    } else if (currentPlayer?.avatar && !currentPlayer.avatar.startsWith('http') && !currentPlayer.avatar.startsWith('/') && !currentPlayer.avatar.startsWith('data:')) {
                      // Emoji avatar (1-4 chars typically)
                      return <span className="text-xl lg:text-2xl">{currentPlayer.avatar}</span>;
                    } else {
                      // Fallback to initial
                      return <span className="text-lg lg:text-xl">{currentPlayer?.pseudo?.charAt(0) || user?.displayName?.charAt(0) || '👤'}</span>;
                    }
                  })()}
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-all shadow-lg active:scale-95 whitespace-nowrap"
              >
                <span className="material-symbols-outlined text-sm">login</span>
                <span className="text-xs lg:text-sm">{t('login.signIn', 'Connexion')}</span>
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};